import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import { CheckboxGroup } from "../../AIAssistants/components";

import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import { useAiSettingsDefaults } from "../../AIAssistants/hooks";

import { SectionLayout, SettingsFormFooter, SettingsToggle, UnsavedChangesTracker } from "../../Components";

type CoachingMeetingsTabProps = {
	setIsUnsafe: () => void;
	setIsSafe: () => void;
};

// Extract keys dynamically from the schemas to stay in sync with schema changes
const getSchemaKeys = <T extends z.ZodObject<z.ZodRawShape>>(schema: T): Array<keyof z.infer<T>> => {
	return Object.keys(schema.shape) as Array<keyof z.infer<T>>;
};

const CoachingMeetingsTab = ({ setIsUnsafe, setIsSafe }: CoachingMeetingsTabProps) => {
	const { value: isPreMeetingPrepFeatureEnabled } = useFeatureFlagQuery(
		"ff-temp-is-ai-pre-meeting-prep-enabled",
		false
	);

	const formSchema = AiSettingsSchema.shape.preMeetingPrepSettings.extend(
		AiSettingsSchema.shape.postMeetingAnalysisSettings.shape
	);
	const formRef = useRef<FormHandle<typeof formSchema>>(null);

	// AI settings data management
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// toggles
	const isPreMeetingPrepEnabled = data?.preMeetingPrepEnabled ?? true;
	const isPostMeetingAnalysisEnabled = data?.postMeetingAnalysisEnabled ?? true;

	// toggle handlers
	const handlePreMeetingPrepToggle = async (val: boolean) => {
		await updateAiSettings({ preMeetingPrepEnabled: val });
		toast.success(`Pre-Meeting Preparation ${val === true ? "Enabled" : "Disabled"}`);
	};

	const handlePostMeetingAnalysisToggle = async (val: boolean) => {
		await updateAiSettings({ postMeetingAnalysisEnabled: val });
		toast.success(`Post Meeting Analysis ${val === true ? "Enabled" : "Disabled"}`);
	};

	// Handle form submission and save changes to AI settings
	const handleSave = async ({ value, resetForm }: { value: z.infer<typeof formSchema>; resetForm: () => void }) => {
		try {
			const preMeetingPrepSettingsKeys = getSchemaKeys(AiSettingsSchema.shape.preMeetingPrepSettings);
			const postMeetingAnalysisSettingsKeys = getSchemaKeys(AiSettingsSchema.shape.postMeetingAnalysisSettings);

			const preMeetingPrepSettings = Object.fromEntries(
				preMeetingPrepSettingsKeys.map((key) => [key, value[key]])
			) as z.infer<typeof AiSettingsSchema.shape.preMeetingPrepSettings>;

			const postMeetingAnalysisSettings = Object.fromEntries(
				postMeetingAnalysisSettingsKeys.map((key) => [key, value[key]])
			) as z.infer<typeof AiSettingsSchema.shape.postMeetingAnalysisSettings>;

			await updateAiSettings({
				preMeetingPrepSettings,
				postMeetingAnalysisSettings,
			});
			toast.success("Your changes have been saved");

			// Reset the form normally - it will use the updated defaultValues from the cache
			resetForm();

			// Mark form as safe after successful save
			setIsSafe();
		} catch {
			toast.error("Unable to save settings");
		}
	};

	// Checkbox options for Pre-Meeting Preparation
	const preMeetingPrepCheckboxOptions = [
		{
			value: "suggestedAgendaItems",
			label: "Suggested Agenda Items",
			dataTestId: "suggested-agenda-items-checkbox",
		},
		{
			value: "goalsForDiscussion",
			label: "Goals For Discussion",
			dataTestId: "goals-for-discussion-checkbox",
		},
		{ value: "recentFeedback", label: "Recent Feedback", dataTestId: "recent-feedback-checkbox" },
		{ value: "recentReviews", label: "Recent Reviews", dataTestId: "recent-reviews-checkbox" },
		{ value: "surveyInsights", label: "Survey Insights", dataTestId: "survey-insights-checkbox" },
	];

	// Checkbox options for Post Meeting Analysis
	const postMeetingAnalysisCheckboxOptions = [
		{ value: "decisionsMade", label: "Decisions Made", dataTestId: "decisions-made-checkbox" },
		{ value: "missedCommitments", label: "Missed Commitments", dataTestId: "missed-commitments-checkbox" },
		{
			value: "participationAndInclusion",
			label: "Participation & Inclusion",
			dataTestId: "participation-and-inclusion-checkbox",
		},
		{
			value: "qualityOfDiscussion",
			label: "Quality of Discussion vs. Monologue",
			dataTestId: "quality-of-discussion-checkbox",
		},
		{ value: "timeManagement", label: "Time Management", dataTestId: "time-management-checkbox" },
		{ value: "meetingCoaching", label: "Meeting Coaching", dataTestId: "meeting-coaching-checkbox" },
	];

	const defaultValues = useAiSettingsDefaults({
		schema: formSchema,
		data,
		extractDefaults: (allDefaults) => ({
			...allDefaults.preMeetingPrepSettings,
			...allDefaults.postMeetingAnalysisSettings,
		}),
		extractData: (data) => ({
			...data.preMeetingPrepSettings,
			...data.postMeetingAnalysisSettings,
		}),
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="coaching-meeting-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={formSchema}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="coaching-meeting-settings-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>
			<SectionLayout
				key="meeting-coaching-section"
				title="Meeting Coaching"
				description="Meeting Coaching provides managers with actionable insights to help them prepare for upcoming meetings and reflect afterward by analyzing meeting transcripts, agendas, notes, created action items, and broader context such as employee goals, performance reviews, feedback, and survey results."
			>
				{isPreMeetingPrepFeatureEnabled && (
					<>
						<SettingsToggle
							key="pre-meeting-prep-toggle"
							id="enable-pre-meeting-preparation"
							label="Enable Pre-Meeting Preparation"
							description="Disabling this setting will hide the ability for managers to generate actionable insights, agenda items, and discussion topics for an upcoming meeting."
							checked={isPreMeetingPrepEnabled}
							onCheckedChange={handlePreMeetingPrepToggle}
							// dataTestId intentionally kept as "ai-coaching-toggle" — QA/e2e locators depend on it
							dataTestId="ai-coaching-toggle"
						/>

						<CheckboxGroup
							key="pre-meeting-prep-content-checkboxes"
							title="Pre-Meeting Preparation Content"
							description="Control each section provided during pre-meeting preparation, to only highlight content that is relevant to you and your company. These options are in addition to sections for current meeting context and a recap of the previous 3 meetings."
							options={preMeetingPrepCheckboxOptions}
						/>
					</>
				)}

				<SettingsToggle
					key="post-meeting-analysis-toggle"
					id="enable-post-meeting-analysis"
					label="Enable Post Meeting Analysis"
					description="Disabling this setting will hide the ability for managers to generate post-meeting analysis content after a meeting."
					checked={isPostMeetingAnalysisEnabled}
					onCheckedChange={handlePostMeetingAnalysisToggle}
					dataTestId="post-meeting-analysis-toggle"
				/>

				<CheckboxGroup
					key="post-meeting-analysis-content-checkboxes"
					title="Post Meeting Analysis Content"
					description="Control each section provided by the AI Coach, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the meeting."
					options={postMeetingAnalysisCheckboxOptions}
				/>
			</SectionLayout>

			<SettingsFormFooter
				key="coaching-meeting-settings-footer"
				formId="coaching-meeting-settings-form"
				isPending={isPending}
			/>
		</PYForm>
	);
};

export default CoachingMeetingsTab;
